import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { List, X, Storefront, SignIn, UserCircle, ArrowRight } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { AutoGlowBrand } from "../../pages/components/AutoGlowLogo";
import LangSwitcher from "../../pages/components/LangSwitcher";
import { getLocale, setLocale } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { useCustomerAuth } from "../../lib/customerAuth";
import { useTheme } from "../../lib/theme";

/**
 * Sprint H · Batch 7 — Header global responsive para TODAS las páginas
 * públicas (Landing, Marketplace, Login, Register, ClienteLogin, ClienteSignup,
 * ForgotPassword, ResetPassword, PublicBooking opcional).
 *
 * Móvil (<768px): hamburguesa + drawer con todas las opciones.
 * Desktop: barra horizontal completa.
 *
 * Las acciones cambian según contexto:
 *  - Sin login: Marketplace · Acceder · "Soy profesional"
 *  - User logueado (taller): Mi panel
 *  - Customer logueado: Mi cuenta
 *
 * `variant` controla el styling:
 *  - "dark"  → fondo oscuro premium (Landing)
 *  - "light" → fondo blanco (logins / forgot / reset / cliente)
 *  - "transparent" → translúcido para hero superpuesto
 */
const NAV_BG = {
  dark:        "bg-[rgba(26,26,26,0.72)] backdrop-blur-md border-b border-[var(--ag-border)] text-[var(--ag-text)]",
  light:       "bg-white/95 backdrop-blur-md border-b border-gray-200 text-[#1A1A1A]",
  transparent: "bg-transparent border-b border-white/10 text-[var(--ag-text)]",
};

export default function GlobalHeader({
  variant = "auto",
  showLogin = true,
  showRegisterCta = true,
  stickyOnMobile = true,
  testId = "global-header",
}) {
  const { user } = useAuth() || {};
  const { account } = useCustomerAuth() || {};
  const { theme } = useTheme() || { theme: "dark" };
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [locale, setLoc] = useState(getLocale());
  // Sprint H Batch 9.3 · variant="auto" sigue al tema activo (light/dark) para
  // garantizar contraste correcto del logo y enlaces en cualquier escenario.
  const effectiveVariant = variant === "auto" ? (theme === "light" ? "light" : "dark") : variant;
  const isLight = effectiveVariant === "light";

  const onLocaleChange = (code) => { setLocale(code); setLoc(code); };

  // Color text helpers según variant
  const linkCls = isLight
    ? "text-sm text-gray-600 hover:text-[var(--ag-gold)] transition-colors"
    : "text-sm text-[var(--ag-text-dim)] hover:text-white transition-colors";

  const ctaTopCls = "ag-btn-gold h-10 px-5";
  const stickyCls = stickyOnMobile ? "sticky top-0 z-40" : "relative z-40";

  // Selecciona estados según rol
  const onMarketplace = location.pathname.startsWith("/marketplace");

  const NAV_LINKS = [
    { to: "/marketplace", label: "Marketplace", testId: "nav-marketplace", active: onMarketplace },
    { to: "/anadir-negocio", label: "Añade tu negocio", testId: "nav-add-business",
      active: location.pathname.startsWith("/anadir-negocio") },
  ];

  return (
    <header
      className={`${stickyCls} ${NAV_BG[effectiveVariant]}`}
      style={{ paddingTop: "env(safe-area-inset-top)" }}
      data-testid={testId}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 sm:h-16 flex items-center justify-between gap-3">
        {/* Logo · Siempre redirige a /welcome (selector cliente/profesional) — Sprint Visual V2 */}
        <Link to="/welcome" className="flex items-center gap-2 shrink-0" data-testid="header-logo" aria-label="Volver al inicio AutoGlow">
          <AutoGlowBrand size={20} />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-5" data-testid="header-desktop-nav">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className={linkCls} data-testid={l.testId}>
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Desktop actions */}
        <div className="hidden md:flex items-center gap-3">
          <LangSwitcher locale={locale} onChange={onLocaleChange} dark={!isLight} />
          {!user && !account && (
            <Link to="/pricing" className={linkCls} data-testid="header-pricing">
              Precios
            </Link>
          )}
          {!user && !account && showLogin && (
            <>
              <Link to="/cliente/login" className={linkCls} data-testid="header-customer-login">
                Mi cuenta
              </Link>
              <Link to="/login" className={linkCls} data-testid="header-pro-login">
                Acceder
              </Link>
            </>
          )}
          {user && (
            <Link to="/" className={linkCls} data-testid="header-back-panel">
              Mi panel
            </Link>
          )}
          {account && !user && (
            <Link to="/mi-cuenta" className={linkCls} data-testid="header-customer-account">
              <UserCircle size={16} weight="duotone" className="inline mr-1" />
              Mi cuenta
            </Link>
          )}
          {!user && !account && showRegisterCta && (
            <Button onClick={() => navigate("/register")} className={ctaTopCls} data-testid="header-cta-register">
              Soy profesional
            </Button>
          )}
        </div>

        {/* Mobile: drawer trigger */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <button
              className={`md:hidden inline-flex items-center justify-center w-10 h-10 rounded-md border ${isLight ? "border-gray-200 text-[#1A1A1A]" : "border-[var(--ag-border)] text-[var(--ag-text)]"}`}
              aria-label="Abrir menú"
              data-testid="header-menu-trigger"
            >
              <List size={20} weight="bold" />
            </button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[280px] sm:w-[320px] h-screen max-h-screen overflow-y-auto bg-[var(--ag-midnight)] text-[var(--ag-text)] border-l border-[var(--ag-border)] p-0 flex flex-col [&>button.absolute]:hidden" data-testid="header-mobile-drawer">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--ag-border)]">
              <AutoGlowBrand size={18} />
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 grid place-items-center rounded-md hover:bg-white/5"
                aria-label="Cerrar menú"
                data-testid="header-menu-close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            <nav className="flex flex-col py-2" data-testid="header-mobile-nav">
              <DrawerLink to="/marketplace" icon={Storefront} onClose={() => setOpen(false)} testId="mobile-nav-marketplace">
                Marketplace
              </DrawerLink>
              {!user && !account && (
                <>
                  <DrawerLink to="/cliente/login" icon={UserCircle} onClose={() => setOpen(false)} testId="mobile-nav-customer-login">
                    Mi cuenta · Cliente
                  </DrawerLink>
                  <DrawerLink to="/login" icon={SignIn} onClose={() => setOpen(false)} testId="mobile-nav-pro-login">
                    Acceder · Profesional
                  </DrawerLink>
                </>
              )}
              {user && (
                <DrawerLink to="/" icon={UserCircle} onClose={() => setOpen(false)} testId="mobile-nav-shop">
                  Mi panel
                </DrawerLink>
              )}
              {account && !user && (
                <DrawerLink to="/mi-cuenta" icon={UserCircle} onClose={() => setOpen(false)} testId="mobile-nav-customer-account">
                  Mi cuenta · Cliente
                </DrawerLink>
              )}
            </nav>

            <div className="border-t border-[var(--ag-border)] mt-2 pt-4 px-5 pb-5 space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Idioma</span>
                <LangSwitcher locale={locale} onChange={onLocaleChange} dark />
              </div>
              {!user && !account && showRegisterCta && (
                <Button
                  onClick={() => { setOpen(false); navigate("/register"); }}
                  className="ag-btn-gold w-full h-11"
                  data-testid="mobile-nav-cta-register"
                >
                  Soy profesional <ArrowRight className="ml-2" weight="bold" />
                </Button>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}

function DrawerLink({ to, icon: Icon, children, onClose, testId }) {
  return (
    <Link
      to={to}
      onClick={onClose}
      className="flex items-center gap-3 px-5 py-3 text-sm text-[var(--ag-text-dim)] hover:bg-white/5 hover:text-white transition-colors"
      data-testid={testId}
    >
      <Icon size={18} weight="duotone" className="text-[var(--ag-gold)]" />
      <span>{children}</span>
    </Link>
  );
}
